import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Car, Home, Users, ArrowRight, Scale, Calculator, Phone, Building, Bike, Gem, Plane, Cross, Key, Ship, PawPrint, HardHat, Wifi, Banknote, X, Activity, Glasses, Stethoscope, Pill, HeartHandshake, Truck, Globe, Baby, CheckCircle, Info, ExternalLink } from 'lucide-react';
import Timer from '../components/Timer';
import GarantieLoyerForm from '../components/forms/GarantieLoyer/GarantieLoyerForm';
import SEO, { SEOPages } from '../components/SEO';

const Assurance = () => {
  const [activeTab, setActiveTab] = useState('mobilite');
  const [showGarantieLoyerForm, setShowGarantieLoyerForm] = useState(false);
  const [prezisaTab, setPrezisaTab] = useState('adultes');

  // Données Prezisa
  const prezisaData = {
    adultes: {
      eco: { prix: 25, sante: 150, lunettes: 100, dentaire: 100, therapies: 200, medicaments: 250, aide: 150, transport: 500, etranger: 1500, bebe: 100, total: 3050 },
      standard: { prix: 30, sante: 200, lunettes: 150, dentaire: 150, therapies: 250, medicaments: 300, aide: 200, transport: 1000, etranger: 2000, bebe: 150, total: 4400 },
      plus: { prix: 40, sante: 300, lunettes: 250, dentaire: 250, therapies: 300, medicaments: 350, aide: 250, transport: 1500, etranger: 2500, bebe: 300, total: 6000 },
      premium: { prix: 55, sante: 400, lunettes: 300, dentaire: 300, therapies: 350, medicaments: 400, aide: 400, transport: 2000, etranger: 4000, bebe: 400, total: 8550 }
    },
    enfants: {
      eco: { prix: 15, sante: 150, lunettes: 100, dentaire: 100, therapies: 200, medicaments: 250, aide: 150, transport: 500, etranger: 1500, bebe: '-', total: 3050 },
      standard: { prix: 20, sante: 200, lunettes: 150, dentaire: 150, therapies: 250, medicaments: 300, aide: 200, transport: 1000, etranger: 2000, bebe: '-', total: 4400 },
      plus: { prix: 30, sante: 300, lunettes: 250, dentaire: 250, therapies: 300, medicaments: 350, aide: 250, transport: 1500, etranger: 2500, bebe: '-', total: 6000 },
      premium: { prix: 45, sante: 400, lunettes: 300, dentaire: 300, therapies: 350, medicaments: 400, aide: 400, transport: 2000, etranger: 4000, bebe: '-', total: 8550 }
    }
  };

  // Catégories d'assurances par onglet (18 types au total)
  const categories = {
    mobilite: {
      label: "Mobilité",
      items: [
        { icon: Car, title: "Voiture", subtitle: "RC, casco, assistance" },
        { icon: Bike, title: "Moto / Vélo", subtitle: "RC, casco, e-bike" },
        { icon: Ship, title: "Bateau", subtitle: "RC nautique, casco" },
        { icon: Plane, title: "Voyage", subtitle: "Annulation, rapatriement" }
      ]
    },
    logement: {
      label: "Logement",
      items: [
        { icon: Home, title: "Ménage", subtitle: "Mobilier, vol, incendie" },
        { icon: Building, title: "Bâtiment", subtitle: "Structure, éléments naturels" },
        { icon: Shield, title: "RC privée", subtitle: "Dommages aux tiers" },
        { icon: HardHat, title: "Construction", subtitle: "Travaux, RC maître d'ouvrage" },
        { icon: Key, title: "Garantie de loyer", subtitle: "Caution locative" },
        { icon: Gem, title: "Objets de valeur", subtitle: "Bijoux, montres, art" }
      ]
    },
    sante: {
      label: "Santé",
      items: [
        { icon: Heart, title: "Maladie", subtitle: "Complémentaire, dentaire" },
        { icon: Cross, title: "Accidents", subtitle: "Privés, loisirs, sport" },
        { icon: Users, title: "Vie & Invalidité", subtitle: "Capital, rente, prévoyance" },
        { icon: Banknote, title: "Indemnités journalières", subtitle: "Perte de gain maladie" },
        { icon: PawPrint, title: "Animaux", subtitle: "Frais vétérinaires, RC" }
      ]
    },
    protection: {
      label: "Protection",
      items: [
        { icon: Scale, title: "Protection juridique", subtitle: "Litiges, frais d'avocat" },
        { icon: Wifi, title: "Cyber", subtitle: "Vol d'identité, fraude en ligne" },
        { icon: Phone, title: "Assistance 24h", subtitle: "Dépannage, aide d'urgence" },
        { icon: Banknote, title: "Cautionnement", subtitle: "Garanties diverses" }
      ]
    }
  };

  return (
    <div className="pt-20">
      <SEO {...SEOPages.assurances} />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Famille suisse protégée par des assurances vie et maladie complémentaire"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/7734601/pexels-photo-7734601.jpeg)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              Assurances : votre <span className="text-blue-300">protection</span> sur mesure
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Conseil indépendant pour optimiser vos assurances. Nous analysons vos besoins 
              et trouvons les meilleures solutions au meilleur prix.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Comparer mes assurances
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact" className="btn-glass text-lg px-8 py-4 flex items-center justify-center">
                Trouver une assurance
              </Link>
            </div>

            {/* Timer */}
            <Timer />
          </div>
        </div>
      </section>

      {/* Section Outils & Calculateurs */}
      <section className="py-16 relative">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold heading-display text-white mb-4">
              <Calculator className="inline-block mr-3 text-blue-400" size={32} />
              Outils & Calculateurs
            </h2>
            <p className="text-gray-300">
              Estimez vos besoins en quelques clics
            </p>
          </div>

          {/* Calculateur Garantie de Loyer - CTA */}
          <div className="max-w-2xl mx-auto">
            <div className="glass p-8 rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-900/20 to-transparent">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                  <Building size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-white">Garantie de loyer</h3>
                  <p className="text-gray-400">Ne bloquez plus votre argent ! Obtenez votre garantie en quelques clics.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 text-center">
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold text-blue-400">3.6%</p>
                  <p className="text-sm text-gray-400">Prime annuelle</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold text-green-400">24h</p>
                  <p className="text-sm text-gray-400">Délai de traitement</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold text-white">0 CHF</p>
                  <p className="text-sm text-gray-400">Argent bloqué</p>
                </div>
              </div>

              <button
                onClick={() => setShowGarantieLoyerForm(true)}
                className="w-full btn-glass btn-primary py-4 flex items-center justify-center gap-2 text-lg"
              >
                <Calculator size={22} />
                Calculer ma prime et faire une demande
              </button>

              <p className="text-xs text-gray-500 mt-4 text-center">
                Alternative au dépôt bancaire • Acceptée par toutes les régies
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Services - Système d'onglets */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold heading-display text-white mb-4">
              Nos assurances
            </h2>
            <p className="text-gray-300 mb-8">
              Sélectionnez une catégorie
            </p>
            
            {/* Onglets */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {Object.entries(categories).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                    activeTab === key
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Contenu de l'onglet actif */}
          <div className="flex justify-center">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl">
              {categories[activeTab].items.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div 
                    key={index} 
                    className="glass p-6 text-center group hover:bg-white/10 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent size={26} className="text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-xs text-gray-400 leading-relaxed">{item.subtitle}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Section Prezisa - Partenaire Santé */}
      <section className="py-16 bg-gradient-to-b from-black/10 to-transparent">
        <div className="container">
          {/* Header avec logo et badge */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img 
                src="https://asisa.ch/uploads/images/_AUTOxAUTO_crop_center-center_95_none/home-hero-icon.svg" 
                alt="ASISA Prezisa" 
                className="h-12 w-auto"
              />
              <h2 className="text-3xl font-bold heading-display text-white">
                Prezisa - Partenaire Santé
              </h2>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full mb-4">
              <CheckCircle size={16} className="text-green-400" />
              <span className="text-green-300 text-sm font-medium">ATOM Group est intermédiaire officiel ASISA</span>
            </div>
          </div>

          {/* Encart important - Ce n'est pas une assurance */}
          <div className="max-w-3xl mx-auto mb-10">
            <div className="glass p-6 rounded-xl border border-amber-500/30 bg-amber-500/10">
              <div className="flex items-start gap-4">
                <Info size={24} className="text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Important : Prezisa n'est pas une assurance</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Prezisa est une <strong className="text-white">association d'entraide</strong> (depuis 1966) qui complète votre couverture santé existante. 
                    Elle rembourse jusqu'à <strong className="text-white">50% des frais non couverts</strong> par votre caisse maladie, 
                    sans questionnaire de santé ni restriction d'âge.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-gray-300 mb-6">
              Choisissez votre formule et bénéficiez de remboursements sur vos dépenses de santé
            </p>
            
            {/* Onglets Adultes/Enfants */}
            <div className="flex justify-center gap-2 mb-8">
              <button
                onClick={() => setPrezisaTab('adultes')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  prezisaTab === 'adultes'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                Adultes (dès 19 ans)
              </button>
              <button
                onClick={() => setPrezisaTab('enfants')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  prezisaTab === 'enfants'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                Enfants (jusqu'à 18 ans)
              </button>
            </div>
          </div>

          {/* Tableau comparatif */}
          <div className="max-w-6xl mx-auto">
            <div className="glass rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-red-600/20 to-red-500/20">
                      <th className="text-left p-4 text-white font-semibold">Prestations</th>
                      <th className="text-center p-4 text-white font-semibold">
                        <div className="flex flex-col items-center">
                          <span className="text-lg">ÉCO</span>
                          <span className="text-2xl font-bold text-red-400">{prezisaData[prezisaTab].eco.prix} CHF</span>
                          <span className="text-xs text-gray-400">par mois</span>
                        </div>
                      </th>
                      <th className="text-center p-4 text-white font-semibold">
                        <div className="flex flex-col items-center">
                          <span className="text-lg">STANDARD</span>
                          <span className="text-2xl font-bold text-blue-400">{prezisaData[prezisaTab].standard.prix} CHF</span>
                          <span className="text-xs text-gray-400">par mois</span>
                        </div>
                      </th>
                      <th className="text-center p-4 text-white font-semibold">
                        <div className="flex flex-col items-center">
                          <span className="text-lg">PLUS</span>
                          <span className="text-2xl font-bold text-green-400">{prezisaData[prezisaTab].plus.prix} CHF</span>
                          <span className="text-xs text-gray-400">par mois</span>
                        </div>
                      </th>
                      <th className="text-center p-4 text-white font-semibold">
                        <div className="flex flex-col items-center">
                          <span className="text-lg">PREMIUM</span>
                          <span className="text-2xl font-bold text-yellow-400">{prezisaData[prezisaTab].premium.prix} CHF</span>
                          <span className="text-xs text-gray-400">par mois</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {[
                      { label: "Soins de santé généraux", key: 'sante', icon: Stethoscope },
                      { label: "Lunettes et lentilles", key: 'lunettes', icon: Glasses },
                      { label: "Soins dentaires", key: 'dentaire', icon: Activity },
                      { label: "Thérapies alternatives", key: 'therapies', icon: HeartHandshake },
                      { label: "Médicaments non remboursés", key: 'medicaments', icon: Pill },
                      { label: "Aide et soins à domicile", key: 'aide', icon: Heart },
                      { label: "Transport et sauvetage", key: 'transport', icon: Truck },
                      { label: "Soins à l'étranger", key: 'etranger', icon: Globe },
                      ...(prezisaTab === 'adultes' ? [{ label: "Prime bébé (1ère année)", key: 'bebe', icon: Baby }] : [])
                    ].map((row, index) => {
                      const IconComponent = row.icon;
                      return (
                        <tr key={index} className="border-t border-white/10 hover:bg-white/5 transition-colors">
                          <td className="p-4 text-gray-300 flex items-center gap-3">
                            <IconComponent size={18} className="text-gray-400" />
                            {row.label}
                          </td>
                          <td className="p-4 text-center text-white font-medium">
                            {prezisaData[prezisaTab].eco[row.key] === '-' ? '-' : `${prezisaData[prezisaTab].eco[row.key]} CHF`}
                          </td>
                          <td className="p-4 text-center text-white font-medium">
                            {prezisaData[prezisaTab].standard[row.key] === '-' ? '-' : `${prezisaData[prezisaTab].standard[row.key]} CHF`}
                          </td>
                          <td className="p-4 text-center text-white font-medium">
                            {prezisaData[prezisaTab].plus[row.key] === '-' ? '-' : `${prezisaData[prezisaTab].plus[row.key]} CHF`}
                          </td>
                          <td className="p-4 text-center text-white font-medium">
                            {prezisaData[prezisaTab].premium[row.key] === '-' ? '-' : `${prezisaData[prezisaTab].premium[row.key]} CHF`}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-white/20 bg-white/5">
                      <td className="p-4 text-white font-bold">Total prestations annuelles</td>
                      <td className="p-4 text-center text-red-400 font-bold text-lg">{prezisaData[prezisaTab].eco.total} CHF</td>
                      <td className="p-4 text-center text-blue-400 font-bold text-lg">{prezisaData[prezisaTab].standard.total} CHF</td>
                      <td className="p-4 text-center text-green-400 font-bold text-lg">{prezisaData[prezisaTab].plus.total} CHF</td>
                      <td className="p-4 text-center text-yellow-400 font-bold text-lg">{prezisaData[prezisaTab].premium.total} CHF</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Avantages Prezisa */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="glass p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Sans questionnaire de santé</h3>
                <p className="text-gray-400 text-sm">Adhésion possible quel que soit votre âge ou état de santé</p>
              </div>
              <div className="glass p-6 text-center">
                <Users className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Réductions famille</h3>
                <p className="text-gray-400 text-sm">2 pers: -5% • 3 pers: -7.5% • 4+ pers: -10%</p>
              </div>
              <div className="glass p-6 text-center">
                <Globe className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Libre choix du praticien</h3>
                <p className="text-gray-400 text-sm">Consultez qui vous voulez, même à l'étranger</p>
              </div>
            </div>

            {/* CTA Prezisa - Lien externe ASISA */}
            <div className="text-center mt-12">
              <a 
                href="https://asisa.ch/fr/devenir-membre-prive?rid=atom-group"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-green-500/30"
              >
                <HeartHandshake size={22} />
                Devenir membre Prezisa
                <ExternalLink size={18} />
              </a>
              <p className="text-sm text-gray-400 mt-4">
                Inscription en ligne sur le site officiel ASISA • Paiement annuel -2% • Semestriel -1%
              </p>
              <a 
                href="https://asisa.ch/uploads/images/Offre-en-Detail.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mt-4 text-sm transition-colors"
              >
                <ArrowRight size={16} />
                Télécharger l'offre détaillée (PDF)
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Processus - Version Compacte */}
      <section className="py-16 bg-gradient-to-b from-transparent to-black/20">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold heading-display text-white mb-4">
              Notre accompagnement
            </h2>
            <p className="text-gray-300">
              Une approche en 4 étapes
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { step: "1", title: "Analyse", desc: "Évaluation de vos besoins" },
              { step: "2", title: "Comparaison", desc: "Étude des offres du marché" },
              { step: "3", title: "Recommandation", desc: "Solutions adaptées" },
              { step: "4", title: "Suivi", desc: "Accompagnement continu" }
            ].map((item, index) => (
              <div key={index} className="glass p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Contact */}
      <section className="py-16">
        <div className="container">
          <div className="glass p-10 rounded-2xl text-center bg-gradient-to-r from-blue-900/30 to-transparent border border-blue-500/20">
            <Phone className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold heading-display text-white mb-4">
              Besoin d'un conseil personnalisé ?
            </h2>
            <p className="text-gray-300 mb-6 max-w-xl mx-auto">
              Nos experts analysent gratuitement vos contrats et vous proposent des solutions optimisées.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-glass btn-primary px-8 py-3 flex items-center justify-center gap-2">
                Demander une analyse gratuite
                <ArrowRight size={18} />
              </Link>
              <a href="tel:+41325391627" className="btn-glass px-8 py-3 flex items-center justify-center gap-2">
                <Phone size={18} />
                +41 32 539 16 27
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Modale Formulaire Garantie de Loyer */}
      {showGarantieLoyerForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto">
          <div className="min-h-screen px-4 py-8 flex items-start justify-center">
            <div className="relative w-full max-w-3xl">
              <button
                onClick={() => setShowGarantieLoyerForm(false)}
                className="absolute top-4 right-4 z-10 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
              <GarantieLoyerForm onClose={() => setShowGarantieLoyerForm(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assurance;